import { useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { GitCompare, CheckCircle2, ImageOff, Package, Columns3, Pencil, RotateCcw, Sparkles, Save, X } from 'lucide-react';
import { useDataStore } from '@/store/useDataStore';
import { parseImageUrls, joinImageUrls, buildSkuName } from '@/services/excelService';
import { Badge } from '@/components/ui/Tag';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import { FIRST_IMAGE_FIELD } from '@/lib/constants';
import { ImageEditorModal } from '@/components/ImageEditorModal';
import type { DataRow, ExcelField } from '@/types';

// --- 差异检测 ---

function isCellDifferent(field: ExcelField, oldValue: string, newValue: string): boolean {
    if (field.isImage) {
        const oldUrls = parseImageUrls(oldValue);
        const newUrls = parseImageUrls(newValue);
        if (oldUrls.length !== newUrls.length) return true;
        return JSON.stringify(oldUrls) !== JSON.stringify(newUrls);
    }
    return oldValue !== newValue;
}

function getImageDiff(oldValue: string, newValue: string, imageReplacements: Map<string, string>) {
    const oldUrls = parseImageUrls(oldValue);
    const newUrls = parseImageUrls(newValue);
    const oldSet = new Set(oldUrls);
    const newSet = new Set(newUrls);

    // 构建反向映射：newUrl → oldUrl
    const reverseReplacements = new Map<string, string>();
    for (const [old, rep] of imageReplacements) {
        reverseReplacements.set(rep, old);
    }

    const replaced: Array<{ oldUrl: string; newUrl: string }> = [];
    const added: string[] = [];
    const removed: string[] = [];
    const common: string[] = [];

    // 分类旧 URL：被替换的、被删除的、未变化的
    const replacedOldSet = new Set<string>();
    for (const oldUrl of oldUrls) {
        const replacement = imageReplacements.get(oldUrl);
        if (replacement && newSet.has(replacement)) {
            replaced.push({ oldUrl, newUrl: replacement });
            replacedOldSet.add(oldUrl);
        } else if (newSet.has(oldUrl)) {
            common.push(oldUrl);
        } else {
            removed.push(oldUrl);
        }
    }

    // 分类新 URL：被替换的（已处理）、真正新增的
    const replacedNewSet = new Set(replaced.map(r => r.newUrl));
    for (const newUrl of newUrls) {
        if (replacedNewSet.has(newUrl)) continue;
        if (oldSet.has(newUrl)) continue; // common
        // 检查反向映射：这个新 URL 是否是某个旧 URL 的替换
        const oldFromReverse = reverseReplacements.get(newUrl);
        if (oldFromReverse && oldSet.has(oldFromReverse) && !replacedOldSet.has(oldFromReverse)) {
            replaced.push({ oldUrl: oldFromReverse, newUrl });
            replacedOldSet.add(oldFromReverse);
        } else {
            added.push(newUrl);
        }
    }

    return { added, removed, common, replaced };
}

// 聚合多行数据中某个图片字段的所有 URL（去重，保持顺序）
function aggregateImageUrls(rows: DataRow[], fieldKey: string): string[] {
    const allUrls: string[] = [];
    const seen = new Set<string>();
    for (const row of rows) {
        const urls = parseImageUrls(row?.[fieldKey] ?? '');
        for (const url of urls) {
            if (!seen.has(url)) {
                seen.add(url);
                allUrls.push(url);
            }
        }
    }
    return allUrls;
}

// 检查多行快照中某个字段是否有任何差异
function isFieldDifferentAcrossRows(field: ExcelField, beforeRows: DataRow[], afterRows: DataRow[]): boolean {
    if (field.isImage) {
        const oldUrls = aggregateImageUrls(beforeRows, field.key);
        const newUrls = aggregateImageUrls(afterRows, field.key);
        if (oldUrls.length !== newUrls.length) return true;
        return JSON.stringify(oldUrls) !== JSON.stringify(newUrls);
    }
    // 文本字段：任意一行不同即视为有差异（一个产品可能含多行 SKU，需逐行对比）
    return beforeRows.some((row, i) =>
        isCellDifferent(field, row?.[field.key] ?? '', afterRows[i]?.[field.key] ?? ''),
    );
}

// --- 产品分组 ---

interface CompareGroup {
    productId: string;
    productName: string;
    originalProductName: string;
    firstImage: string;
    beforeRows: DataRow[];
    afterRows: DataRow[];
    diffFields: ExcelField[];
}

// --- 字段选择器 ---

function FieldSelector({ fields, selectedKeys, onToggle, onToggleAll }: {
    fields: ExcelField[];
    selectedKeys: Set<string>;
    onToggle: (key: string) => void;
    onToggleAll: () => void;
}) {
    const [open, setOpen] = useState(false);
    const allSelected = fields.every(f => selectedKeys.has(f.key));
    return (
        <div className="relative">
            <button onClick={() => setOpen(o => !o)} className="flex items-center gap-1 rounded border border-zinc-200 px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-50">
                <Columns3 size={12} />
                字段筛选
                <span className="rounded bg-zinc-100 px-1 text-[10px] font-mono">{selectedKeys.size}/{fields.length}</span>
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 top-full z-50 mt-1 max-h-60 w-48 overflow-auto rounded-lg border border-zinc-200 bg-white py-1 shadow-lg">
                        <button onClick={onToggleAll} className="w-full px-3 py-1.5 text-left text-xs font-medium text-zinc-500 hover:bg-zinc-50">
                            {allSelected ? '取消全选' : '全选'}
                        </button>
                        <div className="border-t border-zinc-100" />
                        {fields.map(f => (
                            <label key={f.key} className="flex cursor-pointer items-center gap-2 px-3 py-1 text-xs hover:bg-zinc-50">
                                <input type="checkbox" checked={selectedKeys.has(f.key)} onChange={() => onToggle(f.key)} className="rounded border-zinc-300" />
                                <span className="text-zinc-700">{f.label}</span>
                            </label>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

// --- 缩略图 ---

function Thumb({ url, ring, onPreview, originalUrl, size = 'md' }: { url: string; ring?: string; onPreview: (url: string) => void; originalUrl?: string; size?: 'sm' | 'md' }) {
    const [err, setErr] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const hideTimer = useRef<number | null>(null);
    const dim = size === 'sm' ? 'h-8 w-8' : 'h-10 w-10';
    if (err) return <div className={cn('flex items-center justify-center rounded border border-zinc-200 bg-zinc-50', dim)}><ImageOff size={12} className="text-zinc-300" /></div>;

    const showPreview = () => {
        if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; }
        const rect = imgRef.current?.getBoundingClientRect();
        if (rect) {
            const W = 312;
            let left = rect.right + 14;
            let top = rect.top;
            if (left + W > window.innerWidth) left = Math.max(12, rect.left - W - 14);
            if (top + W > window.innerHeight) top = Math.max(12, window.innerHeight - W - 12);
            setPos({ left, top });
        }
        setHovered(true);
    };
    const scheduleHide = () => {
        if (hideTimer.current) clearTimeout(hideTimer.current);
        hideTimer.current = window.setTimeout(() => setHovered(false), 120);
    };

    return (
        <>
            <img
                ref={imgRef}
                src={url}
                alt=""
                className={cn(dim, 'cursor-pointer rounded object-cover transition-transform duration-150 hover:scale-110', ring)}
                onClick={(e) => { e.stopPropagation(); onPreview(url); }}
                onError={() => setErr(true)}
                onMouseEnter={showPreview}
                onMouseLeave={scheduleHide}
            />
            {hovered && pos && createPortal(
                <div
                    className="pointer-events-auto fixed z-[300] cursor-zoom-in animate-fadeIn"
                    style={{ left: pos.left, top: pos.top }}
                    onClick={(e) => { e.stopPropagation(); onPreview(url); }}
                    onMouseEnter={showPreview}
                    onMouseLeave={scheduleHide}
                >
                    {originalUrl ? (
                        <div className="flex gap-2 rounded-lg border border-zinc-200 bg-white p-1.5 shadow-2xl">
                            <div className="flex flex-col items-center gap-0.5">
                                <span className="text-[10px] font-medium text-zinc-400">原图</span>
                                <img src={originalUrl} alt="" className="h-72 w-72 rounded-md border border-zinc-100 object-contain bg-zinc-50" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            </div>
                            <div className="flex flex-col items-center gap-0.5">
                                <span className="text-[10px] font-medium text-amber-500">修改后</span>
                                <img src={url} alt="" className="h-72 w-72 rounded-md border border-amber-200 object-contain bg-zinc-50" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            </div>
                        </div>
                    ) : (
                        <img src={url} alt="" className="h-80 w-80 rounded-lg border border-zinc-200 bg-white object-contain shadow-2xl ring-2 ring-white/80" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    )}
                    <div className="mt-1 flex justify-center">
                        <span className="rounded-full bg-black/60 px-2 py-0.5 text-[10px] text-white">点击放大</span>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}

// --- 左右字段对比行（紧凑，支持编辑/回滚/AI） ---

const ACTION_BTN = 'flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] font-medium transition-colors';

function FieldCompare({ field, beforeRows, afterRows, onPreview, imageReplacements, onRollback, onEdit, onEditImage, onAiTitle, onAiImage }: {
    field: ExcelField;
    beforeRows: DataRow[];
    afterRows: DataRow[];
    onPreview: (url: string) => void;
    imageReplacements: Map<string, string>;
    onRollback?: () => void;
    onEdit?: (value: string) => void;
    onEditImage?: () => void;
    onAiTitle?: () => void;
    onAiImage?: () => void;
}) {
    const original = field.isImage
        ? joinImageUrls(aggregateImageUrls(beforeRows, field.key))
        : (beforeRows[0]?.[field.key] ?? '');
    const current = field.isImage
        ? joinImageUrls(aggregateImageUrls(afterRows, field.key))
        : (afterRows[0]?.[field.key] ?? '');

    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(current);

    if (!isCellDifferent(field, original, current)) return null;
    const isDeleted = !current || (field.isImage && parseImageUrls(current).length === 0);
    const showAiTitle = field.key === 'productName' && !!onAiTitle;
    const showAiImage = field.key === FIRST_IMAGE_FIELD.key && !!onAiImage;

    const actionCol = (extra?: React.ReactNode) => (
        <div className="flex shrink-0 items-center gap-1">
            {extra}
            {!editing && (
                <>
                    {(onEdit || onEditImage) && (
                        <button onClick={() => { if (field.isImage && onEditImage) onEditImage(); else { setDraft(current); setEditing(true); } }} title="编辑此修改项"
                            className={cn(ACTION_BTN, 'text-zinc-500 hover:bg-zinc-100 hover:text-indigo-600')}><Pencil size={12} /> 编辑</button>
                    )}
                    {onRollback && (
                        <button onClick={onRollback} title="回滚为原始数据"
                            className={cn(ACTION_BTN, 'text-zinc-500 hover:bg-red-50 hover:text-red-600')}><RotateCcw size={12} /> 回滚</button>
                    )}
                </>
            )}
        </div>
    );

    if (field.isImage) {
        const diff = getImageDiff(original, current, imageReplacements);
        const originalUrls = parseImageUrls(original);
        const replacedOldSet = new Set(diff.replaced.map(r => r.oldUrl));

        return (
            <div className="grid grid-cols-[auto_1fr_1fr] items-start gap-x-3 border-b border-zinc-100 py-2 last:border-b-0">
                <span className="shrink-0 pt-1 text-xs font-medium text-zinc-500">{field.label}</span>
                {/* 原始数据 */}
                <div className="flex flex-wrap gap-1.5">
                    {originalUrls.length === 0 ? (
                        <span className="text-xs text-zinc-400">--</span>
                    ) : (
                        originalUrls.map((url, i) => {
                            let ring = 'ring-1 ring-zinc-200';
                            if (replacedOldSet.has(url)) ring = 'animate-ringBlink';
                            return <Thumb key={`o-${i}`} url={url} ring={ring} onPreview={onPreview} />;
                        })
                    )}
                </div>
                {/* 修改后 + 操作 */}
                <div className="flex flex-col gap-1.5">
                    <div className="flex flex-wrap gap-1.5">
                        {diff.replaced.map((r, i) => (
                            <Thumb key={`r-${i}`} url={r.newUrl} ring="animate-ringBlink" onPreview={onPreview} originalUrl={r.oldUrl} />
                        ))}
                        {diff.added.map((url, i) => (
                            <Thumb key={`a-${i}`} url={url} ring="ring-1 ring-green-400" onPreview={onPreview} />
                        ))}
                        {diff.common.map((url, i) => (
                            <Thumb key={`c-${i}`} url={url} ring="ring-1 ring-zinc-200" onPreview={onPreview} />
                        ))}
                    </div>
                    {actionCol(showAiImage ? (
                        <button onClick={onAiImage} title="AI 重新编辑首图"
                            className={cn(ACTION_BTN, 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100')}><Sparkles size={12} /> AI 首图</button>
                    ) : undefined)}
                </div>
            </div>
        );
    }

    // 文本字段：逐行对比（一个产品可能含多行 SKU，需一一对比）
    const rowDiffs = afterRows.map((row, i) => {
        const before = beforeRows[i]?.[field.key] ?? '';
        const after = row?.[field.key] ?? '';
        return { before, after, changed: isCellDifferent(field, before, after) };
    });
    const changedRows = rowDiffs.filter(r => r.changed);

    // 产品级字段（如产品名称）在所有 SKU 行取值一致，聚合成单行对比，避免逐 SKU 重复展示
    const beforeUniform = beforeRows.length > 0 && beforeRows.every(r => (r?.[field.key] ?? '') === (beforeRows[0]?.[field.key] ?? ''));
    const afterUniform = afterRows.length > 0 && afterRows.every(r => (r?.[field.key] ?? '') === (afterRows[0]?.[field.key] ?? ''));
    const shouldAggregate = field.key === 'productName' || (beforeUniform && afterUniform);

    // 仅单行有变化（或产品级字段聚合）：沿用原有简洁对比
    if (changedRows.length <= 1 || shouldAggregate) {
        const only = changedRows[0] ?? { before: beforeRows[0]?.[field.key] ?? '', after: afterRows[0]?.[field.key] ?? '' };
        const singleDeleted = !only.after;
        return (
            <div className="grid grid-cols-[auto_1fr_1fr] items-start gap-x-3 border-b border-zinc-100 py-1.5 last:border-b-0">
                <span className="shrink-0 pt-1 text-xs font-medium text-zinc-500">{field.label}</span>
                <p className="break-words text-sm text-red-800 line-through decoration-red-300/60" title={only.before}>{only.before || '—'}</p>
                <div className="min-w-0">
                    {editing ? (
                        <div className="space-y-1.5">
                            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3}
                                className="w-full resize-y rounded-md border border-indigo-300 px-2 py-1 text-sm text-zinc-800 outline-none focus:ring-2 focus:ring-indigo-100" />
                            <div className="flex gap-1.5">
                                <button onClick={() => { onEdit?.(draft); setEditing(false); }}
                                    className={cn(ACTION_BTN, 'bg-indigo-600 text-white hover:bg-indigo-700')}><Save size={12} /> 保存</button>
                                <button onClick={() => { setEditing(false); setDraft(current); }}
                                    className={cn(ACTION_BTN, 'text-zinc-500 hover:bg-zinc-100')}><X size={12} /> 取消</button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-start justify-between gap-2">
                            <p className={cn('min-w-0 flex-1 break-words text-sm font-medium', singleDeleted ? 'text-zinc-400 line-through' : 'text-green-800')} title={only.after}>{only.after || '—'}</p>
                            {actionCol(showAiTitle ? (
                                <button onClick={onAiTitle} title="AI 重新优化标题"
                                    className={cn(ACTION_BTN, 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100')}><Sparkles size={12} /> AI 标题</button>
                            ) : undefined)}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // 多行（多个 SKU）各自不同：展示逐行对比列表
    return (
        <div className="border-b border-zinc-100 py-2 last:border-b-0">
            <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-zinc-500">
                {field.label}
                <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-normal text-zinc-400">共 {changedRows.length} 个 SKU 不同</span>
            </div>
            <div className="space-y-1 rounded-lg bg-zinc-50/60 p-2">
                <div className="grid grid-cols-[auto_auto_1fr_1fr] gap-x-3 text-[9px] font-medium uppercase tracking-wider text-zinc-400">
                    <span></span>
                    <span>SKU</span>
                    <span>原始数据</span>
                    <span>修改后</span>
                </div>
                {rowDiffs.map((r, i) => {
                    if (!r.changed) return null;
                    const imgUrl = parseImageUrls(String((afterRows[i] ?? beforeRows[i])?.skuImage ?? ''))[0] || parseImageUrls(String((afterRows[i] ?? beforeRows[i])?.firstImage ?? ''))[0] || '';
                    return (
                        <div key={i} className="grid grid-cols-[auto_auto_1fr_1fr] items-center gap-x-3">
                            <div className="flex w-8 shrink-0 justify-center">
                                {imgUrl ? <Thumb url={imgUrl} size="sm" onPreview={onPreview} /> : <span className="text-[10px] text-zinc-300">—</span>}
                            </div>
                            <span className="max-w-[110px] truncate text-[11px] font-medium text-zinc-600" title={buildSkuName(afterRows[i] ?? beforeRows[i], i)}>{buildSkuName(afterRows[i] ?? beforeRows[i], i)}</span>
                            <p className="truncate text-xs text-red-800 line-through decoration-red-300/60" title={r.before}>{r.before || '—'}</p>
                            <p className="truncate text-xs font-medium text-green-800" title={r.after}>{r.after || '—'}</p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// --- 产品分组对比（紧凑，不折叠） ---

function GroupCompare({ group, selectedFieldKeys, onPreview, even, imageReplacements }: { group: CompareGroup; selectedFieldKeys: Set<string>; onPreview: (url: string) => void; even: boolean; imageReplacements: Map<string, string> }) {
    const isTitleModified = group.originalProductName !== group.productName && group.originalProductName && group.productName;

    // 过滤已选字段
    const diffFields = group.diffFields.filter(f => selectedFieldKeys.has(f.key));

    // 统计图片增删改
    const imgStats = useMemo(() => {
        let added = 0, removed = 0, replaced = 0;
        for (const field of diffFields) {
            if (!field.isImage) continue;
            const oldVal = joinImageUrls(aggregateImageUrls(group.beforeRows, field.key));
            const newVal = joinImageUrls(aggregateImageUrls(group.afterRows, field.key));
            const diff = getImageDiff(oldVal, newVal, imageReplacements);
            added += diff.added.length;
            replaced += diff.replaced.length;
            removed += diff.removed.length;
        }
        return { added, removed, replaced };
    }, [group, diffFields, imageReplacements]);

    if (diffFields.length === 0 && !isTitleModified) return null;

    return (
        <div className={cn("rounded-lg border border-zinc-200", even && "bg-zinc-50/50")}>
            {/* 产品头 */}
            <div className={cn("flex items-center gap-2 px-3 py-2", even ? "bg-zinc-100/80" : "bg-zinc-50")}>
                <Package size={14} className="text-indigo-400" />
                {group.firstImage && <img src={group.firstImage} alt="" className="h-6 w-6 rounded border border-zinc-200 object-cover" />}
                <span className="font-mono text-xs text-zinc-500">{group.productId}</span>
                <div className="ml-auto flex items-center gap-1.5">
                    {imgStats.removed > 0 && <Badge variant="error" className="text-[10px]">-{imgStats.removed}图</Badge>}
                    {imgStats.replaced > 0 && <Badge variant="warning" className="text-[10px]">~{imgStats.replaced}改</Badge>}
                    {imgStats.added > 0 && <Badge variant="success" className="text-[10px]">+{imgStats.added}图</Badge>}
                    <span className="text-xs text-zinc-400">{group.beforeRows.length}SKU</span>
                </div>
            </div>

            {/* 标题对比 */}
            {isTitleModified && (
                <div className="grid grid-cols-[auto_1fr_1fr] gap-x-3 border-t border-zinc-100 px-3 py-2">
                    <span className="shrink-0 text-xs font-medium text-zinc-500">标题</span>
                    <p className="truncate text-sm text-red-800 line-through decoration-red-300/60" title={group.originalProductName}>{group.originalProductName}</p>
                    <p className="truncate text-sm font-medium text-green-800" title={group.productName}>{group.productName}</p>
                </div>
            )}

            {/* 字段对比 - 扁平列表，图片聚合所有行 */}
            {diffFields.length > 0 && (
                <div className="border-t border-zinc-100 px-3 py-1.5">
                    {diffFields.map((field) => (
                        <FieldCompare
                            key={field.key}
                            field={field}
                            beforeRows={group.beforeRows}
                            afterRows={group.afterRows}
                            onPreview={onPreview}
                            imageReplacements={imageReplacements}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

// --- 主弹窗 ---

interface CompareModalProps {
    open: boolean;
    onClose: () => void;
}

export function CompareModal({ open, onClose }: CompareModalProps) {
    const { fields, originalRows, rows, imageReplacements } = useDataStore();
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorImageUrl, setEditorImageUrl] = useState('');

    // 字段选择状态：只展示有变更的字段
    const allModifiedFields = useMemo(() => {
        const modified = new Set<string>();

        const origByProduct = new Map<string, DataRow[]>();
        const curByProduct = new Map<string, DataRow[]>();

        originalRows.forEach((row, i) => {
            const pid = (row.productId ?? '').trim() || `__noid_${i}__`;
            if (!origByProduct.has(pid)) origByProduct.set(pid, []);
            origByProduct.get(pid)!.push(row);
        });

        rows.forEach((row, i) => {
            const pid = (row.productId ?? '').trim() || `__noid_orig_${i}__`;
            if (!curByProduct.has(pid)) curByProduct.set(pid, []);
            curByProduct.get(pid)!.push(row);
        });

        const allPids = new Set([...origByProduct.keys(), ...curByProduct.keys()]);
        for (const pid of allPids) {
            const beforeRows = origByProduct.get(pid) ?? [];
            const afterRows = curByProduct.get(pid) ?? [];
            for (const f of fields) {
                if (isFieldDifferentAcrossRows(f, beforeRows, afterRows)) {
                    modified.add(f.key);
                }
            }
        }
        return modified;
    }, [fields, originalRows, rows]);

    const [selectedFieldKeys, setSelectedFieldKeys] = useState<Set<string>>(new Set());
    // 当数据变化时重置选中为全部有变更的字段
    const modifiedKey = Array.from(allModifiedFields).sort().join(',');
    useMemo(() => {
        setSelectedFieldKeys(new Set(allModifiedFields));
    }, [modifiedKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleField = (key: string) => {
        setSelectedFieldKeys(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const toggleAllFields = () => {
        setSelectedFieldKeys(prev => {
            if (prev.size === allModifiedFields.size) return new Set();
            return new Set(allModifiedFields);
        });
    };

    // 可选字段列表（只有有变更的）
    const selectableFields = useMemo(() => fields.filter(f => allModifiedFields.has(f.key)), [fields, allModifiedFields]);

    // 按产品分组
    const { groups, stats } = useMemo(() => {
        const map = new Map<string, CompareGroup>();

        const origByProduct = new Map<string, DataRow[]>();
        const curByProduct = new Map<string, DataRow[]>();

        originalRows.forEach((row, i) => {
            const pid = (row.productId ?? '').trim() || `__noid_${i}__`;
            if (!origByProduct.has(pid)) origByProduct.set(pid, []);
            origByProduct.get(pid)!.push(row);
        });

        rows.forEach((row, i) => {
            const pid = (row.productId ?? '').trim() || `__noid_orig_${i}__`;
            if (!curByProduct.has(pid)) curByProduct.set(pid, []);
            curByProduct.get(pid)!.push(row);
        });

        const allPids = new Set([...origByProduct.keys(), ...curByProduct.keys()]);
        let modifiedCellCount = 0;

        for (const pid of allPids) {
            const beforeRows = origByProduct.get(pid) ?? [];
            const afterRows = curByProduct.get(pid) ?? [];

            const diffFields = fields.filter(f => isFieldDifferentAcrossRows(f, beforeRows, afterRows));
            if (diffFields.length === 0) continue;

            modifiedCellCount += diffFields.length;

            const firstRow = afterRows[0] ?? beforeRows[0] ?? {} as DataRow;

            map.set(pid, {
                productId: pid,
                productName: afterRows[0]?.productName ?? beforeRows[0]?.productName ?? '',
                originalProductName: beforeRows[0]?.productName ?? '',
                firstImage: firstRow.firstImage ?? parseImageUrls(firstRow.mainImages ?? '')[0] ?? '',
                beforeRows,
                afterRows,
                diffFields,
            });
        }

        const groupList = Array.from(map.values());
        return { groups: groupList, stats: { modifiedCells: modifiedCellCount, modifiedProducts: groupList.length } };
    }, [fields, originalRows, rows]);

    return (
        <>
            <Modal open={open} onClose={onClose} title="优化前后对比" width="full" className="h-[calc(100vh-4rem)] max-h-[calc(100vh-4rem)]">
                {groups.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-50">
                            <CheckCircle2 size={32} className="text-green-500" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-zinc-700">数据未修改</p>
                            <p className="mt-1 text-xs text-zinc-500">当前数据与原始数据一致，无需对比</p>
                        </div>
                    </div>
                ) : (
                    <div className="flex min-h-0 flex-1 flex-col">
                        {/* 工具栏 */}
                        <div className="flex shrink-0 items-center gap-2 border-b border-zinc-200 pb-2 mb-2">
                            <GitCompare size={16} className="text-indigo-500" />
                            <Badge variant="default" className="text-xs">{stats.modifiedProducts} 产品</Badge>
                            <Badge variant="warning" className="text-xs">{stats.modifiedCells} 字段</Badge>
                            <div className="mx-1 h-3 w-px bg-zinc-200" />
                            <span className="flex items-center gap-1 text-[11px] text-zinc-400"><span className="inline-block h-2.5 w-2.5 rounded bg-zinc-200" />未变化</span>
                            <span className="flex items-center gap-1 text-[11px] text-zinc-400"><span className="inline-block h-2.5 w-2.5 rounded bg-green-300" />新增</span>
                            <span className="flex items-center gap-1 text-[11px] text-zinc-400"><span className="inline-block h-2.5 w-2.5 rounded bg-red-300" />删除</span>
                            <div className="ml-auto">
                                <FieldSelector fields={selectableFields} selectedKeys={selectedFieldKeys} onToggle={toggleField} onToggleAll={toggleAllFields} />
                            </div>
                        </div>

                        {/* 列头 */}
                        <div className="sticky top-0 z-10 grid grid-cols-[auto_1fr_1fr] gap-x-3 border-b border-zinc-200 bg-white/90 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-400 backdrop-blur-sm">
                            <span className="w-14">字段</span>
                            <span>原始数据</span>
                            <span>修改后</span>
                        </div>

                        <div className="flex-1 overflow-auto">
                            <div className="flex flex-col gap-2.5 p-1.5">
                                {groups.map((group, idx) => (
                                    <GroupCompare key={group.productId} group={group} selectedFieldKeys={selectedFieldKeys} onPreview={(url) => { setEditorImageUrl(url); setEditorOpen(true); }} even={idx % 2 === 1} imageReplacements={imageReplacements} />
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
            <ImageEditorModal open={editorOpen} onClose={() => setEditorOpen(false)} imageUrl={editorImageUrl} readOnly />
        </>
    );
}

// --- 单产品对比弹窗 ---

interface ProductCompareModalProps {
    productId: string | null;
    open: boolean;
    onClose: () => void;
    onAiTitle?: (productId: string) => void;
    onAiImage?: (productId: string) => void;
}

export function ProductCompareModal({ productId, open, onClose, onAiTitle, onAiImage }: ProductCompareModalProps) {
    const { fields, originalRows, rows, imageReplacements, updateCell } = useDataStore();
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorImageUrl, setEditorImageUrl] = useState('');
    const [imgEditOpen, setImgEditOpen] = useState(false);
    const [imgEditUrl, setImgEditUrl] = useState('');
    const [imgEditFieldKey, setImgEditFieldKey] = useState('');

    const { diffFields, beforeRows, afterRows, beforeIndices, afterIndices } = useMemo(() => {
        if (!productId || !open) return { diffFields: [] as ExcelField[], beforeRows: [] as DataRow[], afterRows: [] as DataRow[], beforeIndices: [] as number[], afterIndices: [] as number[] };

        const before: DataRow[] = [];
        const after: DataRow[] = [];
        const bIdx: number[] = [];
        const aIdx: number[] = [];

        originalRows.forEach((row, i) => {
            const pid = (row.productId ?? '').trim() || `__noid_${i}__`;
            if (pid === productId) { before.push(row); bIdx.push(i); }
        });
        rows.forEach((row, i) => {
            const pid = (row.productId ?? '').trim() || `__noid_${i}__`;
            if (pid === productId) { after.push(row); aIdx.push(i); }
        });

        if (before.length === 0 && after.length === 0) return { diffFields: [] as ExcelField[], beforeRows: [] as DataRow[], afterRows: [] as DataRow[], beforeIndices: [] as number[], afterIndices: [] as number[] };

        const allFields = [FIRST_IMAGE_FIELD, ...fields];
        const diff = allFields.filter(f => isFieldDifferentAcrossRows(f, before, after));
        return { diffFields: diff, beforeRows: before, afterRows: after, beforeIndices: bIdx, afterIndices: aIdx };
    }, [productId, open, fields, originalRows, rows]);

    const productInfo = useMemo(() => {
        const firstRow = afterRows[0] ?? beforeRows[0] ?? {} as DataRow;
        const firstImage = firstRow.firstImage ?? parseImageUrls(firstRow.mainImages ?? '')[0] ?? '';
        const productName = afterRows[0]?.productName ?? beforeRows[0]?.productName ?? '';
        return { firstImage, productName };
    }, [beforeRows, afterRows]);

    // 回滚某字段到原始值（该产品所有被修改的行）
    const rollbackField = (fieldKey: string) => {
        const n = Math.min(beforeRows.length, afterRows.length);
        for (let i = 0; i < n; i++) {
            updateCell(afterIndices[i], fieldKey, beforeRows[i]?.[fieldKey] ?? '');
        }
    };

    // 编辑文本类修改项：仅更新当前值与原始值不同的行
    const saveEditText = (fieldKey: string, value: string) => {
        const n = Math.min(beforeRows.length, afterRows.length);
        for (let i = 0; i < n; i++) {
            const cur = afterRows[i]?.[fieldKey] ?? '';
            const orig = beforeRows[i]?.[fieldKey] ?? '';
            if (cur !== orig) updateCell(afterIndices[i], fieldKey, value);
        }
    };

    // 编辑图片类修改项：替换该图片 URL（保留其余图片）
    const startImageEdit = (fieldKey: string) => {
        const firstUrl = parseImageUrls(afterRows[0]?.[fieldKey] ?? '')[0];
        if (!firstUrl) return;
        setImgEditFieldKey(fieldKey);
        setImgEditUrl(firstUrl);
        setImgEditOpen(true);
    };

    const saveImageEdit = (newUrl: string) => {
        if (!imgEditFieldKey) return;
        const fieldKey = imgEditFieldKey;
        const oldUrl = parseImageUrls(imgEditUrl)[0] ?? imgEditUrl;
        const n = Math.min(beforeRows.length, afterRows.length);
        for (let i = 0; i < n; i++) {
            const cur = afterRows[i]?.[fieldKey] ?? '';
            if (cur.includes(oldUrl)) {
                updateCell(afterIndices[i], fieldKey, cur.split(oldUrl).join(newUrl));
            }
        }
        setImgEditOpen(false);
    };

    return (
        <>
            <Modal open={open} onClose={onClose} title={`产品对比 · ${productId ?? ''}`} width="xl">
                {diffFields.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-50">
                            <CheckCircle2 size={32} className="text-green-500" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-zinc-700">该产品无修改</p>
                            <p className="mt-1 text-xs text-zinc-500">此产品数据与原始数据一致</p>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {/* 头部信息 */}
                        <div className="flex items-center gap-3 rounded-lg border border-zinc-200 bg-zinc-50/60 px-3 py-3">
                            <Package size={18} className="text-indigo-500" />
                            {productInfo.firstImage && (
                                <img src={productInfo.firstImage} alt="" className="h-9 w-9 rounded border border-zinc-200 object-cover" />
                            )}
                            <span className="font-mono text-sm text-zinc-600">{productId}</span>
                            {productInfo.productName && (
                                <>
                                    <div className="mx-1 h-3 w-px bg-zinc-200" />
                                    <span className="max-w-[200px] truncate text-sm text-zinc-500" title={productInfo.productName}>{productInfo.productName}</span>
                                </>
                            )}
                        </div>

                        {/* 前后对比 */}
                        <div>
                            <div className="mb-2 flex items-center gap-2">
                                <h3 className="text-sm font-semibold text-zinc-600">前后对比</h3>
                                <span className="flex items-center gap-1 text-[11px] text-zinc-400">
                                    <span className="inline-block h-2.5 w-2.5 rounded bg-red-200" />原始
                                </span>
                                <span className="flex items-center gap-1 text-[11px] text-zinc-400">
                                    <span className="inline-block h-2.5 w-2.5 rounded bg-green-200" />修改后
                                </span>
                                <span className="ml-auto text-[11px] text-zinc-400">支持编辑 / 回滚 / AI 重跑</span>
                            </div>
                            <div className="rounded-lg border border-zinc-200">
                                {/* 列头 */}
                                <div className="grid grid-cols-[auto_1fr_1fr] gap-x-3 border-b border-zinc-200 bg-zinc-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                                    <span className="w-14">字段</span>
                                    <span>原始数据</span>
                                    <span>修改后</span>
                                </div>
                                <div className="px-3 py-1.5">
                                    {diffFields.map((field) => (
                                        <FieldCompare
                                            key={field.key}
                                            field={field}
                                            beforeRows={beforeRows}
                                            afterRows={afterRows}
                                            onPreview={(url) => { setEditorImageUrl(url); setEditorOpen(true); }}
                                            imageReplacements={imageReplacements}
                                            onRollback={() => rollbackField(field.key)}
                                            onEdit={(v) => saveEditText(field.key, v)}
                                            onEditImage={() => startImageEdit(field.key)}
                                            onAiTitle={onAiTitle ? () => onAiTitle(productId ?? '') : undefined}
                                            onAiImage={field.key === FIRST_IMAGE_FIELD.key ? (onAiImage ? () => onAiImage(productId ?? '') : undefined) : undefined}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
            <ImageEditorModal open={editorOpen} onClose={() => setEditorOpen(false)} imageUrl={editorImageUrl} readOnly />
            <ImageEditorModal open={imgEditOpen} onClose={() => setImgEditOpen(false)} imageUrl={imgEditUrl} onSave={saveImageEdit} />
        </>
    );
}
